import { useState, VFC } from "react";

import { Add, ExpandMore } from "@material-ui/icons";
import { Button, Collapse, FloatInput, FormControl, FormLabel, Grid, Typography } from "@portex-pro/ui-components";
import { AirPackageGroup, VolumeFormat } from "api/rest/quote-requests";
import { ShipmentPackagingType } from "app/pages/shipments/types/domain";
import { Guide } from "components/Guide";
import PositiveNumberInput from "components/PositiveNumberInput";
import Text from "components/Text";
import capitalize from "lodash/capitalize";
import FormSelectView from "pages/shipper/pages/request-quote/components/FormSelectView";
import { FormProvider, useController, useFieldArray, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { removeFromArray } from "utils/removeFromArray";

import { ButtonGroup } from "../../_components";
import { StepBlock } from "../../_components/StepBlock";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { AirPackageGroupSchema, FinalSchema } from "../_schemas";

export const packagingTypeList = Object.values(ShipmentPackagingType);

interface CargoDetailsStepTagProps {
  airPackageGroups: AirPackageGroupSchema[];
}

const CargoDetailsStepTag: React.FC<CargoDetailsStepTagProps> = ({ airPackageGroups }) => {
  const { t } = useTranslation(["quoteRequest"]);

  if (!airPackageGroups || airPackageGroups.length === 0) {
    return null;
  }

  const firstGroup = airPackageGroups[0];
  if (!firstGroup) {
    return null;
  }

  const parts = [];

  if (firstGroup.item_quantity && firstGroup.packaging_type) {
    const translatedPackagingType = t(`quoteRequest:packagingTypes.${firstGroup.packaging_type}`, {
      defaultValue: capitalize(firstGroup.packaging_type).replaceAll(/_/g, " "),
    });
    parts.push(`${firstGroup.item_quantity} x ${translatedPackagingType}`);
  }

  const weightText = firstGroup.weight_per_package ? `${firstGroup.weight_per_package.toLocaleString()} kg` : null;

  if (parts.length === 0 && !weightText) {
    return null;
  }

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      {parts.length > 0 && <span className="text-blue-600 font-bold text-sm">{parts.join("  ")}</span>}
      {weightText && <span className="text-blue-600 font-normal text-sm">&nbsp;{weightText}</span>}
    </div>
  );
};

const PackageGroup: VFC<{ packageGroup: AirPackageGroupSchema; index: number; methods: UseFormReturn<FinalSchema> }> =
  ({ index, methods }) => {
    const { t } = useTranslation(["quoteRequest", "common", "shipper"]);
    const airPackageGroups = useQuoteRequestSelector(
      (store) => store.quoteRequestSliceV2.quoteRequest.load_spec?.air_package_groups
    );
    const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
    const [open, setOpen] = useState(true);

    const {
      fieldState,
      field: { value: packageGroup },
    } = useController({
      name: `airPackageGroups.${index}`,
      control: methods.control,
    });

    const update = (changes: Partial<AirPackageGroup>) => {
      const newAirPackageGroups = [...(airPackageGroups ?? [])];
      newAirPackageGroups[index] = { ...newAirPackageGroups[index], ...packageGroup, ...changes };
      setQuoteRequestAction({ load_spec: { air_package_groups: newAirPackageGroups } });
    };

    const remove = () => {
      setQuoteRequestAction({
        load_spec: { air_package_groups: airPackageGroups ? removeFromArray(airPackageGroups, index) : undefined },
      });
    };

    return (
      <div
        className={`border-2 rounded w-full max-w-[1000px] ${fieldState.error ? "border-red-500" : "border-gray-200"}`}
      >
        <div
          onClick={() => setOpen(!open)}
          className="w-full cursor-pointer flex flex-row items-center justify-between p-2 pl-4 border-b"
        >
          <Text size="medium" weight="bold">
            {t("common:item%n", { n: index + 1 })}
          </Text>
          <div className="flex items-center space-x-2">
            <Button onClick={remove} style={{ marginLeft: "auto" }}>
              Remove
            </Button>
            <ExpandMore
              className="cursor-pointer transition-transform duration-200"
              fontSize="large"
              style={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
              onClick={(e) => {
                setOpen(!open);
                e.stopPropagation();
              }}
            />
          </div>
        </div>
        <Collapse in={open}>
          <div className="grid grid-cols-2 gap-x-4 p-4">
            <div>
              <FormSelectView
                items={packagingTypeList}
                formControlProps={{ fullWidth: true }}
                value={packageGroup.packaging_type}
                getItemCopy={(value) => capitalize(value).replaceAll(/_/g, " x ")}
                label={t("common:packingType")}
                onChange={(packagingType) => update({ packaging_type: packagingType })}
              />
            </div>
            <div>
              <FormControl fullWidth>
                <FormLabel>
                  {t("shipper:packageGroupView.totalLabel", {
                    label: capitalize(packageGroup.packaging_type).replaceAll(/_/g, " x "),
                  })}
                </FormLabel>
                <PositiveNumberInput
                  fullWidth
                  value={packageGroup.item_quantity}
                  onChange={(value) => update({ item_quantity: value })}
                  displayZero
                />
              </FormControl>
            </div>

            <div>
              <FormControl margin="normal" fullWidth>
                <FormLabel>{t("shipper:volumeFormat")}</FormLabel>
                <ButtonGroup
                  options={[
                    { value: VolumeFormat.Dimensions, label: t("shipper:dimensions") },
                    { value: VolumeFormat.Volume, label: t("shipper:cbm") },
                  ]}
                  selected={packageGroup.volume_format}
                  onSelect={(format) => update({ volume_format: format as VolumeFormat })}
                />
              </FormControl>
            </div>

            <div>
              {packageGroup.volume_format === VolumeFormat.Dimensions ? (
                <FormControl margin="normal" fullWidth>
                  <FormLabel required>{t("shipper:dimensions(cm)")}</FormLabel>
                  <Grid container spacing={3}>
                    <Grid item sm={4}>
                      {/* @ts-expect-error this is fine */}
                      <Guide showGuide={!packageGroup.length_per_package && !fieldState.error?.length_per_package}>
                        <FloatInput
                          margin="none"
                          fullWidth
                          value={packageGroup.length_per_package}
                          placeholder={t("shipper:length")}
                          // @ts-expect-error this is fine
                          highlight={!!fieldState.error?.length_per_package}
                          // @ts-expect-error this is fine
                          required={!!fieldState.error?.length_per_package}
                          onChange={(newValue) => update({ length_per_package: newValue })}
                          InputProps={{
                            endAdornment: <Typography color="textSecondary">cm</Typography>,
                          }}
                        />
                      </Guide>
                    </Grid>
                    <Grid item sm={4}>
                      {/* @ts-expect-error this is fine */}
                      <Guide showGuide={!packageGroup.width_per_package && !fieldState.error?.width_per_package}>
                        <FloatInput
                          margin="none"
                          fullWidth
                          value={packageGroup.width_per_package}
                          placeholder={t("shipper:width")}
                          // @ts-expect-error this is fine
                          highlight={!!fieldState.error?.width_per_package}
                          // @ts-expect-error this is fine
                          required={!!fieldState.error?.width_per_package}
                          onChange={(newValue) => update({ width_per_package: newValue })}
                          InputProps={{
                            endAdornment: <Typography color="textSecondary">cm</Typography>,
                          }}
                        />
                      </Guide>
                    </Grid>
                    <Grid item sm={4}>
                      {/* @ts-expect-error this is fine */}
                      <Guide showGuide={!packageGroup.height_per_package && !fieldState.error?.height_per_package}>
                        <FloatInput
                          margin="none"
                          fullWidth
                          value={packageGroup.height_per_package}
                          placeholder={t("shipper:height")}
                          // @ts-expect-error this is fine
                          highlight={!!fieldState.error?.height_per_package}
                          // @ts-expect-error this is fine
                          required={!!fieldState.error?.height_per_package}
                          onChange={(newValue) => update({ height_per_package: newValue })}
                          InputProps={{
                            endAdornment: <Typography color="textSecondary">cm</Typography>,
                          }}
                        />
                      </Guide>
                    </Grid>
                  </Grid>
                </FormControl>
              ) : (
                <FloatInput
                  fullWidth
                  margin="normal"
                  decimalPlace={3}
                  InputProps={{
                    endAdornment: <Typography color="textSecondary">cbm</Typography>,
                  }}
                  inputProps={{
                    min: 1,
                    step: "any",
                  }}
                  label={t("shipper:cbmPer", {
                    package: capitalize(packageGroup.packaging_type.replaceAll(/_/g, " x ").replaceAll(/s$/g, "")),
                  })}
                  // @ts-expect-error this is fine
                  highlight={!!fieldState.error?.volume_per_item}
                  // @ts-expect-error this is fine
                  required={!!fieldState.error?.volume_per_item}
                  value={packageGroup.volume_per_item || ""}
                  onChange={(value) => update({ volume_per_item: value })}
                />
              )}
            </div>

            <div>
              {/* @ts-expect-error this is fine */}
              <Guide showGuide={!packageGroup.weight_per_package && !fieldState.error?.weight_per_package}>
                <FloatInput
                  fullWidth
                  margin="normal"
                  decimalPlace={3}
                  inputProps={{ min: 1 }}
                  InputProps={{
                    endAdornment: <Typography color="textSecondary">kg</Typography>,
                  }}
                  required
                  // @ts-expect-error this is fine
                  highlight={!!fieldState.error?.weight_per_package}
                  label={t("common:totalWeight_kg")}
                  value={packageGroup.weight_per_package || ""}
                  onChange={(value) => update({ weight_per_package: value })}
                />
              </Guide>
            </div>
          </div>
        </Collapse>
      </div>
    );
  };

const CargoDetailsStep: VFC<{ methods: UseFormReturn<FinalSchema>; onSubmit: (data: FinalSchema) => void }> = ({
  methods,
  onSubmit,
}) => {
  const { t } = useTranslation(["quoteRequest", "common", "shipper"]);
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const airPackageGroups = useQuoteRequestSelector(
    (store) => store.quoteRequestSliceV2.quoteRequest.load_spec?.air_package_groups
  );
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const { stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const { fields } = useFieldArray({ control: methods.control, name: "airPackageGroups" });
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Watch airPackageGroups for the tag
  const watchedAirPackageGroups = useWatch({
    control: methods.control,
    name: "airPackageGroups",
  });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Shipment);
    setIsCollapsed(true);
  };

  const cargoGroupErrors = methods.formState.errors.airPackageGroups;

  if (stepIndex < QuoteStepIndex.CargoDetails) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col gap-6 w-full">
        <StepBlock
          title={t("common:cargoDetails")}
          headerBoxProps={{ onClick: () => setIsCollapsed(!isCollapsed) }}
          stepNumber={3}
          tag={<CargoDetailsStepTag airPackageGroups={watchedAirPackageGroups || []} />}
          footer={
            stepIndex <= QuoteStepIndex.CargoDetails ? (
              <div className="flex justify-between mt-2 w-full">
                <div />
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={methods.handleSubmit(submit)}
                  style={{ width: 150 }}
                >
                  {t("common:next")}
                </Button>
              </div>
            ) : null
          }
        >
          <Collapse in={!isCollapsed}>
            <div className="flex flex-col space-y-4  mt-6">
              {fields.map((packageGroup, index) => {
                return <PackageGroup packageGroup={packageGroup} key={index} index={index} methods={methods} />;
              })}
              <Button
                style={{ alignSelf: "start" }}
                onClick={() =>
                  setQuoteRequestAction({
                    load_spec: {
                      air_package_groups: [
                        ...(airPackageGroups ?? []),
                        {
                          item_quantity: 1,
                          packaging_type: ShipmentPackagingType.Bags,
                          volume_format: VolumeFormat.Dimensions,
                          commodities: "",
                        },
                      ],
                    },
                  })
                }
              >
                <Add
                  className="bg-blue-100 text-blue-500 rounded-full mr-3 "
                  style={{ width: "32px", height: "32px", padding: "6px" }}
                />
                {t("common:addAnotherItem")}
              </Button>
              {!!cargoGroupErrors && (
                <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">{cargoGroupErrors.message}</p>
              )}
            </div>
          </Collapse>
        </StepBlock>
      </div>
    </FormProvider>
  );
};

export default CargoDetailsStep;
